import React from 'react';
import { Shield, Lock, MessageCircle } from 'lucide-react';
import CertificationBadges from './CertificationBadges';

interface PromiseFeature {
  icon: React.ElementType;
  title: string;
  description: string;
  gradient: string;
}

export default function Promise() {
  const features: PromiseFeature[] = [
    {
      icon: Shield,
      title: "Certified Privacy & Technology Professionals",
      description: "Our team includes experienced and highly skilled privacy professionals certified by the IAPP.",
      gradient: "from-emerald-500 to-teal-500"
    },
    {
      icon: Lock,
      title: "Confidentiality & Data Protection",
      description: "We maintain the highest standards of confidentiality and data protection throughout the assessment process.",
      gradient: "from-teal-500 to-cyan-500"
    },
    {
      icon: MessageCircle,
      title: "Flexible & Affordable",
      description: "Our affordable privacy assessments provide the flexibility to work on your schedule.",
      gradient: "from-cyan-500 to-brand-primary"
    }
  ];

  return (
    <div className="relative bg-gradient-to-b from-gray-50 to-gray-100 py-24 overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute -right-1/4 -top-1/4 w-1/2 h-1/2 bg-gradient-to-br from-brand-primary/30 to-transparent rounded-full blur-3xl" />
        <div className="absolute -left-1/4 -bottom-1/4 w-1/2 h-1/2 bg-gradient-to-tr from-emerald-400/30 to-transparent rounded-full blur-3xl" />
      </div>
      
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          
            <h2 className="text-2xl font-bold mb-4 relative font-montserrat text-gray-700">
              <span className="text-brand-primary"></span> Our Promise
              {/* Decorative underline */}
              <div className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-brand-primary to-transparent opacity-75" />
            </h2>
          
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mt-6">
            We're committed to providing small and medium-sized businesses an efficient and comprehensive privacy compliance risk assessment solution with unmatched expertise and support.
          </p>
          {/* Moved certification badges here */}
          <div className="mt-8">
            <CertificationBadges />
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {features.map((feature, index) => (
            <div 
              key={index} 
              className="group relative bg-white rounded-xl p-8 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
            >
              {/* Top gradient bar */}
              <div className={`absolute top-0 left-0 right-0 h-2 bg-gradient-to-r ${feature.gradient} rounded-t-xl`} />
              
              <div className="text-center">
                {/* Icon with gradient background */}
                <div className="inline-flex mb-6 rounded-xl p-0.5 bg-gradient-to-br from-brand-primary to-emerald-500">
                  <div className="bg-white rounded-xl p-4 group-hover:bg-gradient-to-br group-hover:from-brand-primary group-hover:to-emerald-500 transition-all duration-300">
                    <feature.icon className="w-8 h-8 text-brand-primary group-hover:text-white transition-colors" />
                  </div>
                </div>

                <h3 className="text-xl font-semibold mb-4 group-hover:text-brand-primary transition-colors">
                  {feature.title}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {feature.description}
                </p>
              </div>

              {/* Decorative corner accents */}
              <div className="absolute top-4 right-4 w-2 h-2 border-t-2 border-r-2 border-brand-primary/20 group-hover:border-brand-primary transition-colors" />
              <div className="absolute bottom-4 left-4 w-2 h-2 border-b-2 border-l-2 border-brand-primary/20 group-hover:border-brand-primary transition-colors" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}